import type {
  AbstractPartDefinition,
  Blueprint,
  ProductSettings,
} from "@project/emerge-types";
import pick from "lodash/pick";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { AbstractProductDefinition } from "./AbstractProduct";
import type { Manufacturer } from "./Manufacturer";
import type { OptionDB } from "./OptionDB";
import { useProductStoreContext } from "./ProductStoreContext";

/** Rebuilds a single change may trigger through `normalizeAfterBuild`. */
const MAX_POST_BUILD_CORRECTIONS = 3;

/** Products whose unsettled correction was reported already. */
const productsWarnedUnsettled = new WeakSet<object>();

/**
 * Reports a product whose `normalizeAfterBuild` never settles, once per
 * product: it would otherwise warn on every rebuild. Names the settings keys
 * the correction touches rather than logging the settings themselves.
 */
function warnUnsettledCorrection(product: object, correction: object): void {
  if (productsWarnedUnsettled.has(product)) {
    return;
  }
  productsWarnedUnsettled.add(product);
  console.warn(
    `${product.constructor.name}.normalizeAfterBuild still returned a correction of ${Object.keys(correction).join(", ")} after ${MAX_POST_BUILD_CORRECTIONS} passes; keeping the last build.`,
  );
}

const defaultHiddenPartTypes = [
  "Measurements",
  "Volume",
  "koffer",
  "keyboardcase",
  "haube",
  "truhe",
  "case3d",
  "lcase",
  "singledoor",
  "doubledoor",
];

function withoutHiddenPartType(
  hiddenPartTypes: string[],
  partType: string,
): string[] {
  const next: string[] = [];
  for (const type of hiddenPartTypes) {
    if (type !== partType) {
      next.push(type);
    }
  }
  return next;
}

function getNextHiddenPartTypes(
  hiddenPartTypes: string[],
  partType: string,
  visible: boolean,
) {
  const isCurrentlyHidden = hiddenPartTypes.includes(partType);

  if (visible) {
    return isCurrentlyHidden
      ? withoutHiddenPartType(hiddenPartTypes, partType)
      : hiddenPartTypes;
  }

  return isCurrentlyHidden ? hiddenPartTypes : [...hiddenPartTypes, partType];
}

function getToggledHiddenPartTypes(
  hiddenPartTypes: string[],
  partType: string,
) {
  return hiddenPartTypes.includes(partType)
    ? withoutHiddenPartType(hiddenPartTypes, partType)
    : [...hiddenPartTypes, partType];
}

/**
 * The eyes of the viewer sidebar: which part types are hidden, the edge,
 * ground and diagnostic buffer overlays, and the background scene. They are a
 * view preference, not product state: loading or resetting a product keeps
 * them, only {@link PublicProductApi.setViewVisibility} and the toggles change
 * them.
 */
export interface ViewVisibility {
  hiddenPartTypes: string[];
  showEdges: boolean;
  showGround: boolean;
  /**
   * Lid groups (every group but the body, see
   * {@link AbstractProductDefinition.isLidGroup}) are drawn. Off hides them
   * and everything attached to them, and takes them out of hit testing and
   * the camera fit.
   */
  showLids: boolean;
  showAmbientOcclusionBuffer: boolean;
  selectedSceneId: string | null;
}

/** The eyes a new store starts with: measurements, contents and wireframes hidden. */
export function defaultViewVisibility(): ViewVisibility {
  return {
    hiddenPartTypes: [...defaultHiddenPartTypes],
    showEdges: false,
    showGround: true,
    showLids: true,
    showAmbientOcclusionBuffer: false,
    selectedSceneId: null,
  };
}

export function viewVisibilityOf(state: ViewVisibility): ViewVisibility {
  return {
    hiddenPartTypes: [...state.hiddenPartTypes],
    showEdges: state.showEdges,
    showGround: state.showGround,
    showLids: state.showLids,
    showAmbientOcclusionBuffer: state.showAmbientOcclusionBuffer,
    selectedSceneId: state.selectedSceneId,
  };
}

export enum CameraView {
  FRONT = "front",
  BACK = "back",
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
  ISOMETRIC = "isometric",
}

/**
 * Orientation of the viewer camera around its orbit point, in the convention
 * of three's `Spherical` with +Y up: `azimuth` is the angle around +Y from +Z
 * (`atan2(x, z)` of the direction from the orbit point to the camera, in
 * (-π, π]), `polar` the angle from +Y (`acos(y)`).
 */
export interface CameraAngles {
  azimuth: number;
  polar: number;
}

// Default state for the product store
const getDefaultState = <
  ProductType extends string,
  SettingsType extends ProductSettings<ProductType>,
  BlueprintType extends Blueprint,
  PartDefinitionType extends AbstractPartDefinition<string>,
>(): InternalProductState<
  ProductType,
  SettingsType,
  BlueprintType,
  PartDefinitionType
> => ({
  product: null,
  buildVersion: 0, // Increments on each build to trigger part re-renders
  lidsVersion: 0,
  ...defaultViewVisibility(),
  manufacturer: null,
  optionsDb: null,
  cameraView: CameraView.ISOMETRIC,
  fitHoldCount: 0,
  isUserControllingCamera: false,
  onCameraViewChange: () => Promise.resolve(),
  onCameraFit: () => Promise.resolve(),
  onZoomIn: () => {},
  onZoomOut: () => {},
  cameraAnglesSource: null,
  onSettingsChange: null,
});

export interface PublicProductApi<
  ProductType extends string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> {
  updateUserOptions: (
    options: object,
    opts?: { coalesce?: boolean },
  ) => Promise<void>;
  loadDefaultProduct: (
    type: string,
    variant?: string,
  ) => Promise<AbstractProductDefinition<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > | null>;
  loadProduct: (
    settings: SettingsType,
  ) => Promise<AbstractProductDefinition<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > | null>;
  togglePartVisibility: (partType: string) => void;
  setPartVisibility: (partType: string, visible: boolean) => void;
  toggleShowEdges: () => void;
  toggleShowGround: () => void;
  toggleShowLids: () => void;
  toggleShowAmbientOcclusionBuffer: () => void;
  selectScene: (sceneId: string | null) => void;
  /**
   * Sets all eyes at once, e.g. to a choice the host remembered. Only one of
   * the two diagnostic buffers can be shown; the ambient occlusion buffer wins.
   */
  setViewVisibility: (view: ViewVisibility) => void;
  setCameraView: (view: CameraView) => Promise<void>;
  /** Marks the camera as no longer showing a named view (e.g. after orbiting). */
  clearCameraView: () => void;
  /**
   * Written by the viewer camera only: see `trackUserCameraControl`. Writes
   * nothing when the value does not change.
   */
  setUserControllingCamera: (controlling: boolean) => void;
  fitCamera: () => Promise<void>;
  /**
   * Suspends automatic camera fitting until the returned release is called.
   * Take a hold around a change that is about to move the product but has not
   * started yet (opening a lid, waiting for its group to be registered):
   * without it the camera would frame the product as it stands now and never
   * re-frame, because bounds look perfectly stable while nothing moves yet.
   * The release is idempotent; fits still time out, so a leaked hold delays a
   * fit but cannot cancel it.
   */
  beginFitHold: () => () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /**
   * The current orientation of the mounted viewer's camera, or `null` while
   * no viewer camera is mounted. Read when needed (e.g. to render a preview
   * from the same angle); it is not a subscribable state.
   */
  getCameraAngles: () => CameraAngles | null;
  /**
   * Clears the product, the camera and the callbacks. The eyes
   * ({@link ViewVisibility}) stay: they are a view preference, not product state.
   */
  reset: () => void;
  /** Notify subscribers that lid open state on the product may have changed. */
  bumpLidsVersion: () => void;
  initialize: (options: {
    manufacturer: Manufacturer<string, string>;
    optionsDb: OptionDB;
  }) => void;
  /** Set a callback to be notified when settings change (e.g., from drag operations) */
  setOnSettingsChange: (callback: ((options: object) => void) | null) => void;
}

export interface InternalProductApi<
  ProductType extends string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> extends PublicProductApi<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > {
  setProduct: (
    product: AbstractProductDefinition<
      SettingsType,
      BlueprintType,
      PartDefinitionType
    > | null,
  ) => void;
  setManufacturer: (manufacturer: Manufacturer<ProductType, string>) => void;
  setOptionsDb: (optionsDb: OptionDB) => void;
  setOnCameraViewChange: (
    onCameraViewChange: (view: CameraView) => Promise<void>,
  ) => void;
  setOnCameraFit: (onCameraFit: () => Promise<void>) => void;
  setOnZoomIn: (onZoomIn: () => void) => void;
  setOnZoomOut: (onZoomOut: () => void) => void;
  /**
   * Makes `source` answer {@link PublicProductApi.getCameraAngles}. The
   * returned release removes it again, unless another viewer camera has
   * replaced it since.
   */
  provideCameraAngles: (source: () => CameraAngles | null) => () => void;
}

export interface PublicProductState {
  hiddenPartTypes: string[];
  showEdges: boolean;
  /**
   * Draws the studio floor under the product — the lit floor patch, its
   * contact pool and the cast shadow. Off leaves the product on the bare
   * backdrop, which is what a screenshot for a quote wants; the floor grid is
   * a separate scene and keeps its own toggle.
   */
  showGround: boolean;
  /** See {@link ViewVisibility.showLids}. */
  showLids: boolean;
  /**
   * Renders the ambient occlusion buffer on its own instead of the composed
   * image. A diagnostic view: AO on a product this convex is a few narrow
   * creases, and it is close to impossible to judge a change to it through
   * a specular aluminium surface.
   */
  showAmbientOcclusionBuffer: boolean;
  selectedSceneId: string | null;
  /** Increments on each build - used for fine-grained re-renders */
  buildVersion: number;
  /**
   * Bumped when a lid animation group opens or closes (SDK or 3D viewer).
   * Subscribe via selector so only lid-state readers re-render.
   */
  lidsVersion: number;
  /**
   * The named view the camera was last sent to, or `null` once the user has
   * orbited away from it. Survives product loads so that loading a different
   * product keeps the viewing angle, and lets the auto-fit converge on the
   * requested view instead of wherever a transition happened to be.
   */
  cameraView: CameraView | null;
  /**
   * Number of callers currently preparing the scene for a fit. While this is
   * above zero the viewer postpones fitting the camera.
   */
  fitHoldCount: number;
  /**
   * The user is controlling the camera: from the press of a pointer gesture
   * until the camera stopped after its release. The renderer keeps its motion
   * budget for that whole time. Read it with `getState()` in a frame callback;
   * the camera requests a frame on every change. It belongs to the product
   * store, not to one canvas: preview stages under the same store read it too.
   */
  isUserControllingCamera: boolean;
}

export interface InternalProductState<
  ProductType extends string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> extends PublicProductState {
  product: AbstractProductDefinition<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > | null;
  manufacturer: Manufacturer<ProductType, string> | null;
  optionsDb: OptionDB | null;
  onCameraViewChange: (view: CameraView) => Promise<void>;
  onCameraFit: () => Promise<void>;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** Answers `getCameraAngles`; set by the mounted viewer camera. */
  cameraAnglesSource: (() => CameraAngles | null) | null;
  /** Callback invoked when settings change (e.g., from drag operations) */
  onSettingsChange: ((options: object) => void) | null;
}

function createProductStoreState<
  ProductType extends string,
  SettingsType extends ProductSettings<ProductType>,
  BlueprintType extends Blueprint,
  PartDefinitionType extends AbstractPartDefinition<string>,
>(
  set: (
    partial:
      | Partial<
          InternalProductState<
            ProductType,
            SettingsType,
            BlueprintType,
            PartDefinitionType
          > &
            InternalProductApi<
              ProductType,
              SettingsType,
              BlueprintType,
              PartDefinitionType
            >
        >
      | ((
          state: InternalProductState<
            ProductType,
            SettingsType,
            BlueprintType,
            PartDefinitionType
          > &
            InternalProductApi<
              ProductType,
              SettingsType,
              BlueprintType,
              PartDefinitionType
            >,
        ) => Partial<
          InternalProductState<
            ProductType,
            SettingsType,
            BlueprintType,
            PartDefinitionType
          > &
            InternalProductApi<
              ProductType,
              SettingsType,
              BlueprintType,
              PartDefinitionType
            >
        >),
  ) => void,
  get: () => InternalProductState<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > &
    InternalProductApi<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    >,
): InternalProductState<
  ProductType,
  SettingsType,
  BlueprintType,
  PartDefinitionType
> &
  InternalProductApi<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > {
  type ProductInstance = AbstractProductDefinition<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  >;

  type PendingCoalesce = {
    product: ProductInstance;
    options: object;
    selectionGeneration: number;
  };

  type ProductBuildRollback = {
    restore: () => void;
  };

  const pendingCoalesceByProduct = new Map<ProductInstance, PendingCoalesce>();
  const flushPromiseByProduct = new Map<ProductInstance, Promise<void>>();
  const buildPromiseByProduct = new Map<ProductInstance, Promise<void>>();
  let productSelectionGeneration = 0;

  const clearCoalescedQueue = () => {
    pendingCoalesceByProduct.clear();
  };

  const isCurrentProductSelection = (
    product: ProductInstance,
    selectionGeneration: number,
  ) =>
    get().product === product &&
    productSelectionGeneration === selectionGeneration;

  const setSelectedProduct = (product: ProductInstance | null) => {
    productSelectionGeneration += 1;
    clearCoalescedQueue();
    // biome-ignore lint/suspicious/noExplicitAny: globalthis doesn't need to be typed
    (globalThis as any).product = product;
    set({ product });
  };

  const runExclusiveBuild = (
    product: ProductInstance,
    buildTask: () => Promise<void>,
  ): Promise<void> => {
    const previousBuild = buildPromiseByProduct.get(product);
    const runBuildTask = async () => {
      if (get().product !== product) {
        return;
      }
      await buildTask();
    };
    const nextBuild =
      previousBuild === undefined
        ? runBuildTask()
        : previousBuild
            .catch(() => {
              // Keep later updates moving even if the previous build failed.
            })
            .then(async () => {
              await runBuildTask();
            });

    buildPromiseByProduct.set(product, nextBuild);

    return nextBuild.finally(() => {
      if (buildPromiseByProduct.get(product) === nextBuild) {
        buildPromiseByProduct.delete(product);
      }
    });
  };

  const createProductBuildRollback = (
    targetProduct: ProductInstance,
  ): ProductBuildRollback => {
    if (
      typeof targetProduct.createBuildStateSnapshot === "function" &&
      typeof targetProduct.restoreBuildStateSnapshot === "function"
    ) {
      const snapshot = targetProduct.createBuildStateSnapshot();
      return {
        restore: () => targetProduct.restoreBuildStateSnapshot(snapshot),
      };
    }

    const previousSettings = targetProduct.settings;
    const previousBlueprint = targetProduct.blueprint;
    const previousPartDefinitions = targetProduct.partDefinitions;
    return {
      restore: () => {
        targetProduct.settings = previousSettings;
        targetProduct.blueprint = previousBlueprint;
        targetProduct.partDefinitions = previousPartDefinitions;
      },
    };
  };

  const applyOptionsAndBuild = async (
    targetProduct: ProductInstance,
    options: object,
    selectionGeneration: number,
    correctionPass = 0,
  ): Promise<void> => {
    if (!isCurrentProductSelection(targetProduct, selectionGeneration)) {
      return;
    }

    const rollback = createProductBuildRollback(targetProduct);
    targetProduct.settings = {
      ...targetProduct.settings,
      ...options,
    };

    if (!isCurrentProductSelection(targetProduct, selectionGeneration)) {
      rollback.restore();
      return;
    }

    try {
      await targetProduct.build();
    } catch (error) {
      rollback.restore();
      throw error;
    }

    if (!isCurrentProductSelection(targetProduct, selectionGeneration)) {
      rollback.restore();
      return;
    }

    // biome-ignore lint/suspicious/noExplicitAny: globalthis doesn't need to be typed
    (globalThis as any).product = targetProduct;

    set((state) => ({ buildVersion: state.buildVersion + 1 }));

    const onSettingsChange = get().onSettingsChange;
    if (onSettingsChange) {
      onSettingsChange(options);
    }

    // Post-build correction: let the product clamp now-invalid settings (e.g. an
    // addable margin pushed outside its drag area by this resize/orientation/
    // count change). Apply the correction and rebuild once so the corrected
    // positions show immediately. The correction is idempotent, so the
    // follow-up build's own normalize returns null and the recursion terminates.
    // The pass limit is a backstop for a product whose correction is not: a
    // correction that keeps flipping would otherwise rebuild, and report the
    // settings, forever.
    const normalized =
      typeof targetProduct.normalizeAfterBuild === "function"
        ? targetProduct.normalizeAfterBuild()
        : null;
    if (
      !normalized ||
      Object.keys(normalized).length === 0 ||
      !isCurrentProductSelection(targetProduct, selectionGeneration)
    ) {
      return;
    }
    if (correctionPass >= MAX_POST_BUILD_CORRECTIONS) {
      warnUnsettledCorrection(targetProduct, normalized);
      return;
    }
    await applyOptionsAndBuild(
      targetProduct,
      normalized,
      selectionGeneration,
      correctionPass + 1,
    );
  };

  const flushPendingBuild = (product: ProductInstance): Promise<void> => {
    const activeFlush = flushPromiseByProduct.get(product);
    if (activeFlush !== undefined) {
      return activeFlush;
    }

    const flushPromise = runExclusiveBuild(product, async () => {
      while (true) {
        const pendingCoalesce = pendingCoalesceByProduct.get(product);
        if (!pendingCoalesce) {
          break;
        }

        const {
          product: targetProduct,
          options: patch,
          selectionGeneration,
        } = pendingCoalesce;
        pendingCoalesceByProduct.delete(product);

        if (!isCurrentProductSelection(targetProduct, selectionGeneration)) {
          continue;
        }

        await applyOptionsAndBuild(targetProduct, patch, selectionGeneration);
      }
    }).finally(async () => {
      if (flushPromiseByProduct.get(product) === flushPromise) {
        flushPromiseByProduct.delete(product);
      }
      if (pendingCoalesceByProduct.has(product)) {
        await flushPendingBuild(product);
      }
    });

    flushPromiseByProduct.set(product, flushPromise);
    return flushPromise;
  };

  return {
    ...getDefaultState<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    >(),
    setProduct: (product) => {
      setSelectedProduct(product);
    },
    updateUserOptions: async (options, opts) => {
      const currentProduct = get().product;
      if (!currentProduct) return;
      const selectionGeneration = productSelectionGeneration;

      if (opts?.coalesce) {
        const pendingCoalesce = pendingCoalesceByProduct.get(currentProduct);
        if (pendingCoalesce) {
          pendingCoalesceByProduct.set(currentProduct, {
            product: currentProduct,
            options: { ...pendingCoalesce.options, ...options },
            selectionGeneration,
          });
        } else {
          pendingCoalesceByProduct.set(currentProduct, {
            product: currentProduct,
            options: { ...options },
            selectionGeneration,
          });
        }
        await flushPendingBuild(currentProduct);
        return;
      }

      if (
        flushPromiseByProduct.has(currentProduct) ||
        pendingCoalesceByProduct.has(currentProduct)
      ) {
        await flushPendingBuild(currentProduct);
      }

      await runExclusiveBuild(currentProduct, () =>
        applyOptionsAndBuild(currentProduct, options, selectionGeneration),
      );
    },
    loadDefaultProduct: async (type: string, variant?: string) => {
      const manufacturer = get().manufacturer;
      const optionDb = get().optionsDb;
      if (!manufacturer || !optionDb) {
        throw new Error(
          "Cannot load product: manufacturer or optionsDb not available. Call initialize first.",
        );
      }
      const productDefinition = manufacturer.productDefinition;

      // Get base product settings
      const productInfo = manufacturer.products[type as ProductType];

      if (!productInfo) {
        throw new ProductTypeNotFoundError(type);
      }

      const alternativeCuts = variant === "cover_inside";
      const productSettings = productInfo.getSerializedDefaultVersion(
        optionDb,
        { alternativeCuts },
      ) as SettingsType;

      const product = new productDefinition(
        productSettings,
        optionDb,
      ) as unknown as AbstractProductDefinition<
        SettingsType,
        BlueprintType,
        PartDefinitionType
      >;

      if (!product) throw new ProductTypeNotFoundError(type);
      await product.build();
      setSelectedProduct(product);

      return product;
    },
    loadProduct: async (settings: SettingsType) => {
      const manufacturer = get().manufacturer;
      const optionDb = get().optionsDb;
      if (!manufacturer || !optionDb) {
        throw new Error(
          "Cannot load product: manufacturer or optionsDb not available. Call initialize first.",
        );
      }
      const productDefinition = manufacturer.productDefinition;
      const product = new productDefinition(
        settings,
        optionDb,
      ) as unknown as AbstractProductDefinition<
        SettingsType,
        BlueprintType,
        PartDefinitionType
      >;
      await product.build();
      setSelectedProduct(product);

      return product;
    },
    setManufacturer: (manufacturer) => set({ manufacturer }),
    setOptionsDb: (optionsDb) => set({ optionsDb }),
    togglePartVisibility: (partType) => {
      const hiddenPartTypes = getToggledHiddenPartTypes(
        get().hiddenPartTypes,
        partType,
      );
      set({ hiddenPartTypes });
    },
    setPartVisibility: (partType, visible) => {
      const currentHiddenPartTypes = get().hiddenPartTypes;
      const hiddenPartTypes = getNextHiddenPartTypes(
        currentHiddenPartTypes,
        partType,
        visible,
      );

      if (hiddenPartTypes !== currentHiddenPartTypes) {
        set({ hiddenPartTypes });
      }
    },
    toggleShowEdges: () => {
      set((state) => ({ showEdges: !state.showEdges }));
    },
    toggleShowGround: () => {
      set((state) => ({ showGround: !state.showGround }));
    },
    toggleShowLids: () => {
      set((state) => ({ showLids: !state.showLids }));
    },
    toggleShowAmbientOcclusionBuffer: () => {
      set((state) => ({
        showAmbientOcclusionBuffer: !state.showAmbientOcclusionBuffer,
      }));
    },
    selectScene: (sceneId) => {
      set({ selectedSceneId: sceneId });
    },
    setViewVisibility: (view) => {
      set(viewVisibilityOf(view));
    },
    bumpLidsVersion: () => {
      set((state) => ({ lidsVersion: state.lidsVersion + 1 }));
    },
    reset: () => {
      productSelectionGeneration += 1;
      clearCoalescedQueue();
      set({
        cameraView: CameraView.ISOMETRIC,
        fitHoldCount: 0,
        lidsVersion: 0,
        product: null,
        manufacturer: null,
        optionsDb: null,
        onCameraViewChange: () => Promise.resolve(),
        onCameraFit: () => Promise.resolve(),
        onSettingsChange: null,
      });
    },
    initialize: ({ manufacturer, optionsDb }) => {
      set({ manufacturer, optionsDb });
    },
    setOnCameraViewChange: (onCameraViewChange) => {
      set({ onCameraViewChange });
    },
    setCameraView: async (view: CameraView) => {
      set({ cameraView: view });
      await get().onCameraViewChange(view);
    },
    clearCameraView: () => {
      // Runs on every camera control event; a no-op write would still notify.
      if (get().cameraView === null) return;
      set({ cameraView: null });
    },
    setUserControllingCamera: (controlling) => {
      if (get().isUserControllingCamera === controlling) return;
      set({ isUserControllingCamera: controlling });
    },
    setOnCameraFit: (onCameraFit) => {
      set({ onCameraFit });
    },
    fitCamera: async () => {
      await get().onCameraFit();
    },
    beginFitHold: () => {
      set((state) => ({ fitHoldCount: state.fitHoldCount + 1 }));

      let released = false;
      return () => {
        if (released) return;
        released = true;
        set((state) => ({ fitHoldCount: Math.max(0, state.fitHoldCount - 1) }));
      };
    },
    setOnZoomIn: (onZoomIn) => {
      set({ onZoomIn });
    },
    zoomIn: () => {
      get().onZoomIn();
    },
    setOnZoomOut: (onZoomOut) => {
      set({ onZoomOut });
    },
    zoomOut: () => {
      get().onZoomOut();
    },
    getCameraAngles: () => get().cameraAnglesSource?.() ?? null,
    provideCameraAngles: (source) => {
      set({ cameraAnglesSource: source });
      return () => {
        if (get().cameraAnglesSource === source) {
          set({ cameraAnglesSource: null });
        }
      };
    },
    setOnSettingsChange: (callback) => {
      set({ onSettingsChange: callback });
    },
  };
}

export function createProductStore<
  ProductType extends string = string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
>() {
  return create<
    InternalProductState<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    > &
      InternalProductApi<
        ProductType,
        SettingsType,
        BlueprintType,
        PartDefinitionType
      >
  >()((set, get) =>
    createProductStoreState<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    >(set, get),
  );
}

/**
 * @deprecated Use ProductStoreProvider and useProductStoreContext instead.
 * This default store is kept for backward compatibility only.
 */
export const __useProductStore = createProductStore<
  string,
  ProductSettings<string>,
  Blueprint,
  AbstractPartDefinition<string>
>();

// Custom error for product type not found
export class ProductTypeNotFoundError extends Error {
  constructor(productType: string) {
    super(`Es existiert kein Produkttyp mit der Bezeichnung "${productType}"`);
    this.name = "ProductTypeNotFoundError";
  }
}

/**
 * Hook that selects a subset of part definition properties by ID from the context store.
 * Must be used within a ProductStoreProvider.
 *
 * @example
 * ```tsx
 * const partDef = usePartDefinition({
 *   id: 'part-123',
 *   select: ['position', 'rotation'] as const
 * });
 * ```
 */
export function usePartDefinition<
  PartType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
  const Keys extends readonly (keyof PartType)[] = readonly (keyof PartType)[],
>(options: { id: string; select: Keys }): Pick<PartType, Keys[number]> | null {
  const useProductStore = useProductStoreContext();
  return useProductStore(
    useShallow((state) => {
      // Include buildVersion in selector to trigger re-evaluation on builds
      // This is needed because we no longer change the product reference on every build
      // (which was causing 100ms React reconciliation on each drag frame)
      void state.buildVersion;

      const definition = state.product?.partDefinitions.get(options.id);

      if (!definition) return null;

      // Use the keys array directly with pick
      return pick(definition as PartType, options.select) as Pick<
        PartType,
        Keys[number]
      >;
    }),
  );
}

/**
 * @deprecated Use useProductStoreContext directly instead.
 * Hook to get the typed product store state from context.
 */
export function useTypedProductStore<
  ProductType extends string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
>() {
  const useProductStore = useProductStoreContext<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  >();
  return useProductStore((state) => state);
}
